/* Iter 31.132f — Deploy-status badge.
 *
 * Renders a small red "REDEPLOY PENDING" stamp when the backend
 * reports the production SHA doesn't match the preview SHA.
 * Silent when in sync, when no comparison endpoint exists, or
 * when the comparison fails.
 *
 * Backend endpoint: GET /api/build/comparison
 *   → { preview_sha, production_sha, in_sync, error }
 *
 * Component is intentionally tiny — drop into any admin header.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle } from "lucide-react";

export default function DeployStatusBadge({ className = "" }) {
    const [state, setState] = useState({ loading: true, in_sync: null, preview_sha: null, production_sha: null, error: null });

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get("/build/comparison");
                if (!alive) return;
                setState({ loading: false, ...data });
            } catch (e) {
                if (!alive) return;
                setState({ loading: false, in_sync: null, error: "fetch failed" });
            }
        })();
        return () => { alive = false; };
    }, []);

    if (state.loading) return null;
    // Silent on success.
    if (state.in_sync) return null;
    // Silent when we can't even reach production (network / first deploy
    // bootstrap) — but only if both SHAs are present. If production_sha
    // is null AND there's a 404 error, that means production hasn't
    // shipped the endpoint yet → ALSO out of sync, show the badge.
    if (!state.preview_sha) return null;

    const shortPreview = (state.preview_sha || "").slice(0, 7);
    const shortProd = state.production_sha ? state.production_sha.slice(0, 7) : "—";

    return (
        <span
            data-testid="deploy-status-badge"
            className={`inline-flex items-center gap-2 px-3 py-1 border-2 border-stamp-red text-stamp-red font-heading uppercase text-[10px] tracking-[0.22em] bg-paper/80 rotate-1 ${className}`}
            title={`Preview ${shortPreview} · Production ${shortProd}${state.error ? ` · ${state.error}` : ""}`}
        >
            <AlertTriangle className="w-3 h-3" strokeWidth={2} />
            Redeploy pending
        </span>
    );
}
